import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Globe, Save, CheckCircle, AlertCircle, RefreshCw, Lock, Database } from "lucide-react";
import { Form, FormField, FormItem, FormLabel, FormControl, FormDescription } from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useConnectionTest } from "./useConnectionTest";
import { useFrankApiConfig } from "@/hooks/useFrankApiConfig";
import { useOrganization } from "@/hooks/useOrganization";
import { toast } from "sonner";

// Current API version
const CURRENT_API_VERSION = "1.0";
const DEFAULT_THRESHOLD = 0.3;
const DEFAULT_API_URL = "https://dirty-priscella-gapthemind-b863fbd4.koyeb.app";

const formSchema = z.object({
  apiUrl: z.string().url({ message: "Please enter a valid URL" }),
  isEnabled: z.boolean(),
  threshold: z.coerce.number().min(0, "Threshold must be at least 0").max(1, "Threshold must be at most 1"),
});

type FormValues = z.infer<typeof formSchema>;

export const FrankApiSettingsForm = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { organizationId, isLoading: isOrgLoading } = useOrganization();
  const { config, isLoading: isLoadingConfig, saveConfig } = useFrankApiConfig(organizationId);
  const { 
    isTestingConnection, 
    lastTestResult, 
    testApiConnection 
  } = useConnectionTest();

  // Debug logging
  useEffect(() => {
    console.log('FrankApiSettingsForm - organizationId:', organizationId);
    console.log('FrankApiSettingsForm - isOrgLoading:', isOrgLoading);
    console.log('FrankApiSettingsForm - config:', config);
  }, [organizationId, isOrgLoading, config]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      apiUrl: DEFAULT_API_URL,
      isEnabled: false,
      threshold: DEFAULT_THRESHOLD,
    },
  });

  // Load the current settings from Supabase when config is loaded
  useEffect(() => {
    if (config) {
      console.log('Loading config into form:', config);
      form.reset({
        apiUrl: config.api_url,
        isEnabled: config.is_enabled,
        threshold: DEFAULT_THRESHOLD,
      });
    } else {
      // Reset to defaults if no config exists
      form.reset({
        apiUrl: DEFAULT_API_URL,
        isEnabled: false,
        threshold: DEFAULT_THRESHOLD,
      });
    }
  }, [config, form]);

  const handleSaveSettings = form.handleSubmit(async (data) => {
    console.log('handleSaveSettings called with data:', data);
    console.log('organizationId:', organizationId);
    
    if (!organizationId) {
      console.error('No organizationId available for saving');
      toast.error('No organization found. Please refresh and try again.');
      return;
    }

    setIsLoading(true);
    console.log('Starting save process...');
    
    try {
      const success = await saveConfig(data.apiUrl, data.isEnabled);
      console.log('Save result:', success);
      
      if (success) {
        // Also update localStorage for backward compatibility with existing Frank chat functionality
        localStorage.setItem("frankApiUrl", data.apiUrl);
        localStorage.setItem("frankApiVersion", CURRENT_API_VERSION);
        
        // Trigger storage event for other components that are listening
        window.dispatchEvent(new Event('storage'));
        
        toast.success('Frank API settings saved successfully!');
        console.log('Settings saved successfully');
      } else {
        toast.error('Failed to save settings');
        console.error('Save operation returned false');
      }
    } catch (error) {
      console.error('Error in handleSaveSettings:', error);
      toast.error('An error occurred while saving settings');
    } finally {
      setIsLoading(false);
    }
  });

  const resetToDefault = async () => {
    if (!organizationId) return;

    form.reset({
      apiUrl: DEFAULT_API_URL,
      isEnabled: false,
      threshold: DEFAULT_THRESHOLD,
    });
    
    // Save to Supabase
    await saveConfig(DEFAULT_API_URL, false);
    
    // Also update localStorage for backward compatibility
    localStorage.setItem("frankApiUrl", DEFAULT_API_URL);
    localStorage.setItem("frankThreshold", DEFAULT_THRESHOLD.toString());
    localStorage.setItem("frankApiVersion", CURRENT_API_VERSION);
    
    // Trigger storage event for other components that are listening
    window.dispatchEvent(new Event('storage'));
  };

  const handleTestConnection = () => {
    const apiUrl = form.getValues("apiUrl");
    testApiConnection(apiUrl);
  };

  if (isOrgLoading || isLoadingConfig) {
    return (
      <Card className="w-full">
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <RefreshCw className="h-4 w-4 animate-spin mr-2" />
            Loading Frank API configuration...
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!organizationId) {
    return (
      <Card className="w-full">
        <CardContent className="p-6">
          <div className="flex items-center justify-center text-red-600">
            <AlertCircle className="h-4 w-4 mr-2" />
            No organization found. Please refresh the page or contact support.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Globe className="h-5 w-5 text-primary" />
          Frank AI Settings
        </CardTitle>
        <CardDescription>
          Configure the API endpoint and parameters for Frank AI integration. 
          Settings are stored in your organization's database.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={handleSaveSettings} className="space-y-6">
            <FormField
              control={form.control}
              name="isEnabled"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base flex items-center gap-2">
                      <Database className="h-4 w-4" />
                      Enable Frank AI Integration
                    </FormLabel>
                    <FormDescription>
                      Enable or disable FAQ synchronization with Frank AI. When disabled, FAQs will only be stored locally.
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="apiUrl"
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <FormLabel>Frank API URL</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Enter Frank API URL"
                      className="w-full"
                      disabled={!form.watch("isEnabled")}
                    />
                  </FormControl>
                  <FormDescription>
                    Using API version {CURRENT_API_VERSION}. 
                    {!form.watch("isEnabled") && " (Disabled - enable integration to configure URL)"}
                  </FormDescription>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="threshold"
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <FormLabel className="flex items-center gap-2">
                    <Lock className="h-4 w-4 text-muted-foreground" />
                    <span>Threshold (Fixed at {DEFAULT_THRESHOLD})</span>
                  </FormLabel>
                  <FormControl>
                    <div className="flex items-center gap-4">
                      <Input
                        {...field}
                        type="number"
                        step="0.1"
                        min="0"
                        max="1"
                        className="w-24 bg-muted cursor-not-allowed"
                        disabled={true}
                        value={DEFAULT_THRESHOLD}
                      />
                      <span className="text-sm text-muted-foreground">(System-enforced value)</span>
                    </div>
                  </FormControl>
                  <FormDescription>
                    The relevancy threshold is fixed at {DEFAULT_THRESHOLD} for all users and associations.
                  </FormDescription>
                </FormItem>
              )}
            />

            {config && (
              <div className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-md">
                <div className="flex items-center gap-2 mb-1">
                  <Database className="h-4 w-4" />
                  <span className="font-medium">Configuration Status</span>
                </div>
                <div>Last updated: {new Date(config.updated_at).toLocaleString()}</div>
                <div>Status: {config.is_enabled ? "Enabled" : "Disabled"}</div>
              </div>
            )}
          
            {lastTestResult.status !== 'none' && (
              <div className={`p-3 rounded-md text-sm ${
                lastTestResult.status === 'success' 
                  ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400' 
                  : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'
              }`}>
                <div className="flex items-center gap-2">
                  {lastTestResult.status === 'success' 
                    ? <CheckCircle className="h-4 w-4" /> 
                    : <AlertCircle className="h-4 w-4" />}
                  <span>{lastTestResult.message}</span>
                </div>
              </div>
            )}

            <CardFooter className="flex justify-between flex-wrap gap-2 px-0">
              <Button 
                type="button"
                onClick={resetToDefault} 
                variant="outline" 
                className="flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Reset to Default
              </Button>
              
              <div className="flex gap-2">
                <Button 
                  type="button"
                  onClick={handleTestConnection} 
                  variant="outline" 
                  disabled={isTestingConnection || !form.watch("apiUrl")}
                  className="flex items-center gap-2"
                >
                  {isTestingConnection ? (
                    <>Testing<span className="animate-pulse">...</span></>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4" />
                      Test Connection
                    </>
                  )}
                </Button>
                <Button 
                  type="submit" 
                  disabled={isLoading || isLoadingConfig} 
                  className="flex items-center gap-2"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {isLoading ? 'Saving...' : 'Save Settings'}
                </Button>
              </div>
            </CardFooter>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default FrankApiSettingsForm;
